/**
 * TriggerHealthPage — Measure pillar analytics surface.
 *
 * Per audit deliverable 06F. Read-only per-trigger health: fire count,
 * success rate, failure breakdown, and live status. Deep-links back to the
 * Marketing Triggers management surface for fixes.
 *
 * Data source:
 *   GET /api/admin/email-analytics/overview?period=<today|Nd|custom&from&to>
 *     → data.triggerHealth: TriggerHealthRow[]
 *     → data.summary.active_triggers: number
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PillarPageShell from "../shared/PillarPageShell";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsQuery } from "../shared/emailAnalyticsRange";
import { describeRange } from "../../common/timeRange";
import { MetricCard } from "../../help";
import apiService from "../../../services/api";
import {
  anyPeriodScoped,
  fmtFires,
  fmtRate,
  rateColor,
  triggerFireView,
  type TriggerFireFields,
} from "../shared/triggerMetrics";

interface TriggerHealthRow extends TriggerFireFields {
  id: string | number;
  trigger_name: string;
  event_type: string;
  times_triggered: number | null;
  times_sent_successfully: number | null;
  times_failed: number | null;
  status: string | null;
  success_rate: number | null;
}

interface TriggerHealthSummary {
  active_triggers: number | null;
}

interface TriggerHealthData {
  triggerHealth: TriggerHealthRow[];
  summary: TriggerHealthSummary;
}

const MANAGE_PATH = "/dashboard/communications/marketing/triggers";

// Sum a numeric field across rows; returns null if there are no rows at all so
// the KPI renders "—" rather than a misleading 0.
const sumField = (
  rows: TriggerHealthRow[] | undefined,
  key: "times_triggered" | "times_failed",
): number | null => {
  if (!rows || rows.length === 0) return null;
  return rows.reduce((acc, r) => acc + (r[key] ?? 0), 0);
};

// Sum the period-scoped fire counts when the backend ships them (M5 wire
// contract), otherwise the lifetime counters.
const sumFires = (rows: TriggerHealthRow[] | undefined): number | null => {
  if (!rows || rows.length === 0) return null;
  return rows.reduce((acc, r) => acc + (triggerFireView(r).fires ?? 0), 0);
};

const statusChipColor = (
  status: string | null,
): "success" | "warning" | "error" | "default" => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
    case "healthy":
    case "enabled":
      return "success";
    case "degraded":
    case "warning":
    case "paused":
      return "warning";
    case "failing":
    case "error":
    case "disabled":
      return "error";
    default:
      return "default";
  }
};

const TriggerHealthPage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" });
  const [data, setData] = useState<TriggerHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res: any = await apiService.get(
          `/api/admin/email-analytics/overview?${emailAnalyticsQuery(period)}`,
        );
        if (!mounted) return;
        const payload = res?.data ?? res ?? {};
        const rows: TriggerHealthRow[] = Array.isArray(payload.triggerHealth)
          ? payload.triggerHealth
          : [];
        const summary: TriggerHealthSummary = payload.summary ?? {
          active_triggers: null,
        };
        setData({ triggerHealth: rows, summary });
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load trigger health");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [period]);

  const rows = data?.triggerHealth ?? [];
  const activeTriggers = data?.summary?.active_triggers ?? null;
  // Only period-scoped once the endpoint ships fires_period; the underlying
  // email_triggers counters are lifetime, so the page must not name the
  // selected window over them.
  const periodScoped = anyPeriodScoped(rows);
  const scopeLabel = periodScoped ? describeRange(period) : "all time";
  const totalFires = sumFires(rows);
  const totalFailures = sumField(rows, "times_failed");

  const fmt = (n: number | null): string =>
    n != null ? n.toLocaleString() : "—";

  return (
    <PillarPageShell
      pillar="measure"
      title="Trigger Health"
      subtitle={`Per-trigger fire count, success rate, and failure breakdown (${scopeLabel}). Read-only — deep-link to Triggers to make a fix.`}
      helpTipId="comm.triggers.title"
      rightActions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TimeRangeFilter value={period} onChange={setPeriod} />
        <Button
          variant="contained"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(MANAGE_PATH)}
          sx={{
            bgcolor: "#1A5F7A",
            "&:hover": { bgcolor: "#144A60" },
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 600,
          }}
        >
          Manage
        </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <MetricCard kpi="trigger.active_count" value={fmt(activeTriggers)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            {/* Scope is carried by the page subtitle and the table header —
                MetricCard's `comparison` only renders alongside a trend chip. */}
            <MetricCard kpi="trigger.total_fires" value={fmt(totalFires)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 5px rgba(0,0,0,0.04)",
                borderLeft: `6px solid ${totalFailures ? "#7D1935" : "#146152"}`,
              }}
            >
              <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 0.5,
                  }}
                >
                  Total Failures
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    color: totalFailures ? "#7D1935" : "#146152",
                    lineHeight: 1.1,
                  }}
                >
                  {fmt(totalFailures)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {totalFailures
                    ? `Across all triggers (${scopeLabel})`
                    : totalFailures === 0
                      ? "No failures — all clear"
                      : "No data yet"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Per-trigger table */}
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 5px rgba(0,0,0,0.04)" }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              Per-Trigger Breakdown
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No trigger activity recorded yet.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell align="right">
                    Fires ({periodScoped ? "period" : "all time"})
                  </TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const fireView = triggerFireView(row);
                  return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.trigger_name ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.event_type ? (
                        <Chip
                          label={row.event_type}
                          size="small"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {fmtFires(fireView.fires)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: rateColor(fireView.successRate),
                        fontWeight: fireView.successRate == null ? 400 : 600,
                      }}
                    >
                      {fmtRate(fireView.successRate)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          row.times_failed && row.times_failed > 0
                            ? "#7D1935"
                            : "text.secondary",
                        fontWeight: row.times_failed ? 600 : 400,
                      }}
                    >
                      {fmt(row.times_failed)}
                    </TableCell>
                    <TableCell>
                      {row.status ? (
                        <Chip
                          label={row.status}
                          size="small"
                          color={statusChipColor(row.status)}
                          variant="outlined"
                          sx={{ textTransform: "capitalize" }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PillarPageShell>
  );
};

export default TriggerHealthPage;
